/**
 * useMutations Hook
 *
 * Get mutation functions for a collection.
 * Resolves the correct scope via ScopeRegistry (multi-scope)
 * or uses the nearest RecordScope context during registry transitions.
 */

import { useCallback, useContext, useMemo } from 'react'
import { RecordContext, useRecordAuth } from '../context'
import { useScopeRegistry } from '../ScopeRegistry'
import { RecordRoomNotReadyError, WriteCancelledError } from '../errors'
import { MSG } from '@/shared/protocol/constants'

/**
 * Get mutation functions for a collection.
 *
 * Type contract:
 *   - `create(data)` requires the **full** record shape `T`. New records
 *     have no existing data to merge with, so all fields the schema
 *     considers required must be present.
 *   - `put(recordId, patch)` accepts a **`Partial<T>`**. The server
 *     merges the patch into the existing row (`{...existing, ...patch}`),
 *     so callers should send only the fields they want to change.
 *
 * @example
 * ```tsx
 * const { create, put, remove } = useMutations<Task>('tasks')
 *
 * // create needs the full Task shape
 * const id = await create({ title: 'New task', completed: false, priority: 1 })
 *
 * // put is a partial update — only the fields you want to change
 * await put(id, { completed: true })
 * await remove(id)
 * ```
 */
export function useMutations<T = unknown>(collection: string): {
  /** True once the collection's RecordRoom can accept writes. Disable the
   *  control on it (`disabled={!ready}`) rather than early-returning from
   *  the handler: an early return drops the user's write silently, while a
   *  write attempted before `ready` at least raises a `not_ready` error. */
  ready: boolean
  create: (data: T) => Promise<string>
  put: (recordId: string, data: Partial<T>) => Promise<void>
  remove: (recordId: string) => Promise<void>
  /** Resolve once the server has applied the write. They survive a dropped
   *  connection: a write made while reconnecting waits and is sent, in
   *  order, when the room is back, and the room applies a resend at most
   *  once. They reject when the server refuses the write; with
   *  `WriteUnconfirmedError` if the room never answers it across several
   *  fresh connections, its safe resend window expires, or its scope closes
   *  after sending; with `not_ready` before the room's first connection; and
   *  with `WriteCancelledError` when it was never sent before the account
   *  changed or scope unmounted, or was made after the scope unmounted. Unsent
   *  writes can wait offline indefinitely; a sent write is not retried after
   *  the server can forget its outcome (just under a day). */
  createConfirmed: (data: T) => Promise<string>
  putConfirmed: (recordId: string, data: Partial<T>) => Promise<void>
  removeConfirmed: (recordId: string) => Promise<void>
} {
  // Resolve through the registry and retain the nearest scope during navigation.
  const registry = useScopeRegistry()
  const recordCtx = useContext(RecordContext)
  // Stable wrapper from RecordProvider — safe in a dependency array.
  const onWriteError = useRecordAuth()?.onWriteError

  const scopeEntry = registry?.resolve(collection) ?? null

  // Prefer RecordContext when this scope handles the collection (avoids stale ScopeRegistry during navigation)
  const preferLocal = recordCtx?.registeredCollections?.has(collection) ?? false
  const sendMessage = preferLocal ? recordCtx!.sendMessage : (scopeEntry?.sendMessage ?? recordCtx?.sendMessage)
  const sendConfirmed = preferLocal ? recordCtx!.sendConfirmed : (scopeEntry?.sendConfirmed ?? recordCtx?.sendConfirmed)
  const ready = preferLocal ? recordCtx!.ready : (scopeEntry?.ready ?? recordCtx?.ready ?? false)

  if (!sendMessage || !sendConfirmed) {
    throw new Error(
      `useMutations('${collection}'): No scope found. Wrap this hook in a RecordScope that registers the collection.`,
    )
  }

  // A write refused for readiness never reaches the server, so `onWriteError`
  // (the one surface an app can observe a rejected fire-and-forget write on)
  // is the only place it can show up; report it *and* throw, so callers that
  // await keep their contract and callers that don't still see the failure.
  const reportNotReady = useCallback((): RecordRoomNotReadyError => {
    onWriteError?.({
      kind: 'not_ready',
      title: 'Not saved — still connecting',
      detail:
        `The room backing "${collection}" was not ready to accept writes yet, so the change was dropped. ` +
        `Gate the action on the \`ready\` flag from useMutations('${collection}') (e.g. \`disabled={!ready}\`) and retry once it is true.`,
    })
    return new RecordRoomNotReadyError(collection)
  }, [collection, onWriteError])

  // The readiness gate for fire-and-forget writes, which a closed socket drops.
  const assertReady = useCallback(() => {
    if (!ready) throw reportNotReady()
  }, [ready, reportNotReady])

  // Confirmed writes skip the gate: once the room has been ready, the socket
  // holds them through a reconnect and sends them in order, so a write made
  // while `ready` is briefly false still lands.
  const confirm = useCallback(
    async (payload: Record<string, unknown>, type: string): Promise<void> => {
      try {
        await sendConfirmed({ type, payload: { collection, ...payload } })
      } catch (error) {
        if (error instanceof RecordRoomNotReadyError) throw reportNotReady()
        if (error instanceof WriteCancelledError) {
          onWriteError?.({
            kind: 'not_ready',
            title: 'Not saved — signed out or closed first',
            detail:
              `A change to "${collection}" was waiting for the connection when the signed-in account changed ` +
              `or the screen holding it closed, so it was never sent. Make the change again if it is still wanted.`,
          })
        }
        throw error
      }
    },
    [sendConfirmed, collection, reportNotReady, onWriteError],
  )

  const create = useCallback(
    async (data: T): Promise<string> => {
      assertReady()
      const recordId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
      sendMessage({ type: MSG.PUT, payload: { collection, recordId, data } })
      return recordId
    },
    [assertReady, sendMessage, collection],
  )

  const put = useCallback(
    async (recordId: string, data: Partial<T>): Promise<void> => {
      assertReady()
      sendMessage({ type: MSG.PUT, payload: { collection, recordId, data } })
    },
    [assertReady, sendMessage, collection],
  )

  const remove = useCallback(
    async (recordId: string): Promise<void> => {
      assertReady()
      sendMessage({ type: MSG.DELETE, payload: { collection, recordId } })
    },
    [assertReady, sendMessage, collection],
  )

  const createConfirmed = useCallback(
    async (data: T): Promise<string> => {
      const recordId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
      await confirm({ recordId, data }, MSG.PUT)
      return recordId
    },
    [confirm],
  )

  const putConfirmed = useCallback(
    (recordId: string, data: Partial<T>) => confirm({ recordId, data }, MSG.PUT),
    [confirm],
  )

  const removeConfirmed = useCallback((recordId: string) => confirm({ recordId }, MSG.DELETE), [confirm])

  // Memoize the return object so consumers get a stable reference.
  // Without this, every render produces a new object, which breaks any
  // useCallback/useEffect that depends on the useMutations() result
  // (e.g., cleanup effects re-fire and delete records they shouldn't).
  return useMemo(
    () => ({ ready, create, put, remove, createConfirmed, putConfirmed, removeConfirmed }),
    [ready, create, put, remove, createConfirmed, putConfirmed, removeConfirmed],
  )
}
